import React, { useState } from 'react';
import { useRouter } from 'next/router';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useMantineTheme, Divider } from '@mantine/core';
import { useMediaQuery } from 'react-responsive';

import { Routes } from '~/domains/common/constants/routes';
import { ButtonLink } from '~/domains/design-system/Button';
import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';
import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import { OauthSSO } from '~/domains/auth/components/SSOButtons/SSOButtons';
import { BRANDS, PLATFORMS } from '~/domains/auth/consts';
import dt from '~/testing';

import { RecaptchaText } from '../RecaptchaText';

import SignInForm, { type SignInFormProps } from './SignInForm';
import useStyles from './SignInActions.styles';

type SignInActionProps = {
  signInProps: SignInFormProps;
  brand?: BRANDS;
  platform?: PLATFORMS;
  resetSuccess?: string;
};

function SignInActions({ brand, platform, signInProps, resetSuccess }: SignInActionProps) {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const { t } = useTranslation('auth');
  const router = useRouter();
  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.sm });
  // TODO: also need to make sure they didn't just try to use SSO
  // does this even need to be a state?
  const [isRYP] = useState(brand === BRANDS.RYP);

  const ssoStatus = router.query.status;
  const ssoUnifiedLoginAttempted = ssoStatus === 'authSSODeprecated';
  const displaySSOButtons = isRYP && !ssoUnifiedLoginAttempted && !resetSuccess;

  return (
    <div className={classes.container}>
      {displaySSOButtons ? (
        <div className={classes.ssoButtonContainer}>
          <Title
            variant="display-headline-expanded-xl"
            c={theme.colors.gray2[6]}
            className={classes.ssoButtonHeader}
          >
            {t('signIn.rypTitle')}
          </Title>
          <OauthSSO>
            <OauthSSO.Facebook />
            <OauthSSO.Google />
            {platform === PLATFORMS.IOS && <OauthSSO.Apple />}
          </OauthSSO>
          <Divider
            color="gray2.0"
            size="xs"
            label={
              <Text variant="body-large" c="gray2.3">
                {t('common.or').toUpperCase()}
              </Text>
            }
            labelPosition="center"
            labelProps={{ color: 'gray2.3' }}
            className={classes.divider}
          />
        </div>
      ) : (
        <SignUpHeader
          title={t('signIn.title')}
          logoSize={30}
          copy=""
          titleVariant="display-headline-expanded-xxl"
          isLogoVisible={!isMobile}
          centered
        />
      )}

      <SignInForm {...signInProps} />

      <Text
        variant="body-large"
        ta="center"
        color={theme.colors.gray2[4]}
        className={classes.textWithLink}
      >
        <Trans
          i18nKey="auth:signIn.signUpCTA"
          components={{
            signUpLink: (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore Children are injected via <Trans> component
              <ButtonLink
                data-test-id={dt.components.shared.auth.signIn.signUpLink}
                className={classes.buttonLink}
                mode="linkButton"
                size="large"
                type="text"
                href={Routes.signUp({
                  redirectTo: router.query.redirectTo as string,
                  brand: router.query.brand as string,
                  platform: router.query.platform as string,
                })}
              />
            ),
          }}
        />
      </Text>

      <RecaptchaText />
    </div>
  );
}

export default SignInActions;
